import parse, { domToReact, HTMLReactParserOptions } from 'html-react-parser';
import {
    highlightGlossaryTerms,
    isPreservedElement,
} from 'src/components/ArtifactDetail/components/TermScanner';
import { useGlossary } from 'src/components/providers/GlossaryProvider';
import ReactPlayer from 'react-player';
import React, { useEffect, useState } from 'react';
import { Tabs } from '@cloudscape-design/components';
import CopyToClipboard from '@cloudscape-design/components/copy-to-clipboard';
import DOMPurify from 'dompurify';

type useRenderContentProps = {
    data: string;
    artifactId: string;
    setModalHref: (href: string) => void;
    loading: boolean;
};
export const useRenderContent = ({
    artifactId,
    data,
    loading,
    setModalHref,
}: useRenderContentProps) => {
    const [artifactHtml, setArtifactHtml] = useState(parse(''));
    const linkElementsRef = React.useRef<HTMLLinkElement[]>([]);
    const { terms: glossaryTerms, isGlossaryEnabled } = useGlossary();

    useEffect(() => {
        if (loading) {
            setArtifactHtml(parse(''));
        }
    }, [loading]);

    const findTabInformation = (child: any, dataTab = ''): any => {
        if (!child.children || !child.children.length) {
            return { child, dataTab };
        }

        const data_tab = child.attribs['data-tab'] || dataTab;
        const nextChoice = child.attribs.role === 'tab' ? 1 : 0;
        return findTabInformation(child.children[nextChoice], data_tab);
    };

    // Helper function to extract text content from elements
    const extractTextContent = (node: any): string => {
        if (!node) return '';
        if (node.type === 'text') return node.data || '';
        if (node.children) {
            return node.children
                .map((child: any) => extractTextContent(child))
                .join('');
        }
        return '';
    };

    const options: HTMLReactParserOptions = {
        replace: (node: any) => {
            // Handle InLineCode and ProgramCode elements - add copy functionality
            if (
                node.attribs &&
                node.attribs.class &&
                (node.attribs.class.includes('InLineCode') ||
                    node.attribs.class.includes('ProgramCode'))
            ) {
                const codeText = extractTextContent(node);

                return (
                    <span className="gru-inline-code-wrapper">
                        <code className={node.attribs.class}>
                            {domToReact(node.children, options)}
                        </code>
                        <CopyToClipboard
                            copyButtonAriaLabel="Copy code"
                            copyButtonText=""
                            copyErrorText="Failed to copy"
                            copySuccessText="Copied"
                            textToCopy={codeText}
                            variant="icon"
                        />
                    </span>
                );
            }

            // This is all hidden or display none. Not sure why it's delivered
            if (node.name === 'script') {
                return <></>;
            }
            if (
                node.attribs &&
                node.attribs.class &&
                (node.attribs.class === 'FragmentSkinComponentWrapper' ||
                    node.attribs.class === 'LoadImgPlaceholder')
            ) {
                return <></>;
            }
            if (
                node.attribs &&
                node.attribs.class &&
                node.attribs.class.includes('HideUntilLoad')
            ) {
                node.attribs.class = node.attribs.class.replace(
                    'HideUntilLoad',
                    ''
                );
                node.attribs.id = `${node.attribs.id}-1`;
            }
            if (
                node.attribs &&
                (node.attribs.class === 'Main' ||
                    node.attribs.class == 'OuterWrap')
            ) {
                node.attribs.class = 'Removed';
                node.attribs.id = `${node.attribs.id}-1`;
            }

            if (
                node.name === 'video' &&
                node.attribs &&
                node.attribs['data-src']
            ) {
                return <ReactPlayer url={node.attribs['data-src']} controls />;
            }

            // Xyleme has some custom code to change display:none to block that we do not
            if (
                node.attribs &&
                node.attribs.class &&
                node.attribs.class.includes('OverlayObjects')
            ) {
                node.attribs.style = 'display: block';
            }
            if (
                node.attribs &&
                node.attribs.class &&
                node.attribs.class.includes('Xref')
            ) {
                const href = node.attribs.href;
                node.attribs.href = `/${artifactId}#/${href}`;
                return (
                    <a
                        className={node.attribs.class}
                        onClick={() => {
                            setModalHref(href);
                        }}
                    >
                        {node.children[0]?.data}
                    </a>
                );
            }

            // Glossary matching on text nodes outside preserved elements.
            // See Requirements 2.1-2.3, 2.7, 2.8. The parent-element check is
            // handled by walking up via node.parent — html-react-parser sets
            // this on every child. We skip if any ancestor is a preserved
            // element (anchor, button, form control, heading, code, pre, or
            // anything with an inline event-handler attribute).
            if (
                isGlossaryEnabled &&
                glossaryTerms.size > 0 &&
                node.type === 'text' &&
                typeof node.data === 'string' &&
                node.data.length > 0
            ) {
                let ancestor = node.parent;
                let inPreserved = false;
                while (ancestor) {
                    if (isPreservedElement(ancestor)) {
                        inPreserved = true;
                        break;
                    }
                    ancestor = ancestor.parent;
                }
                if (!inPreserved) {
                    const parts = highlightGlossaryTerms(
                        node.data,
                        glossaryTerms
                    );
                    if (parts.length !== 1 || typeof parts[0] !== 'string') {
                        return <>{parts}</>;
                    }
                }
            }

            if (
                node.attribs &&
                node.attribs.class &&
                node.attribs.class.includes('Tabs TabsComponent ')
            ) {
                const tabInformation = node.children[0].children.map(
                    (child: any) => {
                        return findTabInformation(child);
                    }
                );
                const tabs = tabInformation.map((tab: any, index: number) => {
                    const content = node.children[1].children[index];
                    return {
                        label: tab.child.data,
                        id: tab.dataTab,
                        content: domToReact(
                            content.children[1].children,
                            options
                        ),
                    };
                });
                return <Tabs tabs={tabs} />;
            }
        },
    };

    // Stylesheet injection is keyed only on `data`. If we also keyed it
    // on glossaryTerms / isGlossaryEnabled (as the parse effect below is),
    // the cleanup would tear down every <link rel="stylesheet"> from
    // document.head when the glossary fetch resolves ~2s after mount,
    // causing a visible FOUC flash. Keeping it in its own effect means
    // glossary arrival only re-parses the body — the stylesheets stay.
    useEffect(() => {
        if (!data) {
            return;
        }
        const matches =
            data.match(
                /<link[^>]*?(rel=["']stylesheet["']|type=["']text\/css["'])[^>]*?>/gi
            ) || [];

        matches.forEach((match) => {
            const hrefMatch = match.match(/href=["']([^"']+)["']/i);
            if (hrefMatch) {
                const linkElement = document.createElement('link');
                linkElement.rel = 'stylesheet';
                linkElement.href = hrefMatch[1];
                linkElement.type = 'text/css';
                document.head.appendChild(linkElement);
                linkElementsRef.current.push(linkElement);
            }
        });

        return () => {
            linkElementsRef.current.forEach((el) =>
                document.head.removeChild(el)
            );
            linkElementsRef.current = [];
        };
    }, [data]);

    // Sanitize + parse is keyed on data AND on the glossary inputs so that
    // highlights appear when the glossary fetch resolves after the body
    // was first rendered. Isolated from stylesheet injection above so a
    // late glossary arrival does NOT retrigger the link teardown.
    useEffect(() => {
        if (!data) {
            return;
        }
        const body = data
            .replace(/<!DOCTYPE[^>]*>/i, '')
            .replace(/<\/?html[^>]*>/gi, '')
            .replace(/<head[\s\S]*?<\/head>/gi, '');

        const cleanedHTML = DOMPurify.sanitize(body, {
            ADD_TAGS: ['iframe'],
            ADD_ATTR: ['data-src'],
        });

        setArtifactHtml(parse(cleanedHTML, options));
    }, [data, glossaryTerms, isGlossaryEnabled]);

    return { artifactHtml };
};
